'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function DueDateCalculator() {
  const t = useTranslations("tools.dueDateCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [calculationMethod, setCalculationMethod] = useState('lmp');
  const [inputs, setInputs] = useState({
    lmpDate: new Date().toISOString().split('T')[0],
    cycleLength: 28,
    ultrasoundDate: new Date().toISOString().split('T')[0],
    ultrasoundWeeks: 12,
    ultrasoundDays: 0,
    conceptionDate: new Date().toISOString().split('T')[0],
    ivfDate: new Date().toISOString().split('T')[0],
    ivfEmbryoAge: 5,
  });
  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ec4899',
    primaryHover: '#f43f5e',
    secondary: '#fce7f3',
    accent: '#be185d',
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(236, 72, 153, 0.2)',
    background: 'linear-gradient(135deg, #fef3f2 0%, #fce7f3 50%, #f0f9ff 100%)',
  };

  const formatDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const calculateWeeksBetween = (date1: Date, date2: Date) => {
    const diffTime = Math.abs(date2.getTime() - date1.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(diffDays / 7);
    const days = diffDays % 7;
    return { weeks, days, totalDays: diffDays };
  };

  const getTrimester = (weeks: number): string => {
    if (weeks <= 13) return t('firstTrimester');
    if (weeks <= 27) return t('secondTrimester');
    return t('thirdTrimester');
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let dueDate: Date;
    let conceptionDate: Date;

    if (calculationMethod === 'lmp') {
      const lmpDate = new Date(inputs.lmpDate);
      const ovulationDay = inputs.cycleLength - 14;
      conceptionDate = addDays(lmpDate, ovulationDay);
      dueDate = addDays(lmpDate, 280);

    } else if (calculationMethod === 'ultrasound') {
      const ultrasoundDate = new Date(inputs.ultrasoundDate);
      const totalDays = (inputs.ultrasoundWeeks * 7) + inputs.ultrasoundDays;
      const daysRemaining = 280 - totalDays;
      dueDate = addDays(ultrasoundDate, daysRemaining);
      conceptionDate = addDays(dueDate, -266);

    } else if (calculationMethod === 'conception') {
      conceptionDate = new Date(inputs.conceptionDate);
      dueDate = addDays(conceptionDate, 266);

    } else {
      const ivfDate = new Date(inputs.ivfDate);
      conceptionDate = addDays(ivfDate, -inputs.ivfEmbryoAge);
      dueDate = addDays(conceptionDate, 266);
    }

    const currentDate = new Date();
    const gestationInfo = calculateWeeksBetween(conceptionDate, currentDate);
    const daysUntilDue = calculateWeeksBetween(currentDate, dueDate).totalDays;

    const trimester1Start = addDays(conceptionDate, -14);
    const trimester1End = addDays(trimester1Start, 13 * 7);
    const trimester2End = addDays(trimester1Start, 27 * 7);
    const trimester3End = dueDate;

    setResults({
      dueDate: formatDate(dueDate),
      currentWeek: `${gestationInfo.weeks} ${t('weeks')}, ${gestationInfo.days} ${t('days')}`,
      currentTrimester: getTrimester(gestationInfo.weeks),
      daysRemaining: daysUntilDue >= 0 ? `${daysUntilDue} ${t('days')}` : t('babyArrived'),
      conceptionEstimate: formatDate(conceptionDate),
      trimester1: `${formatDate(trimester1Start)} - ${formatDate(trimester1End)}`,
      trimester2: `${formatDate(addDays(trimester1End, 1))} - ${formatDate(trimester2End)}`,
      trimester3: `${formatDate(addDays(trimester2End, 1))} - ${formatDate(trimester3End)}`,
    });

    setTimeout(() => {
      document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: colors.background,
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { margin: '0 auto' },
    { maxWidth: '100%' },
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '0.75rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const methodSelectorStyle = {
    marginBottom: '2rem',
    paddingBottom: '1.5rem',
    borderBottom: '2px solid #f1f5f9',
  };

  const labelStyle = {
    display: 'block',
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.75rem',
    fontSize: '1rem',
  };

  const selectStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      color: colors.textPrimary,
      backgroundColor: '#f8fafc',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      color: colors.textPrimary,
      transition: 'all 0.3s ease',
    },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.5rem',
  };

  const inlineInputsStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const radioGroupStyle = getResponsiveStyle(
    { display: 'flex', marginTop: '0.5rem' },
    { flexDirection: 'column' as const, gap: '0.75rem' },
    { flexDirection: 'row' as const, gap: '1.5rem' },
    { flexDirection: 'row' as const, gap: '1.5rem' }
  );

  const radioOptionStyle = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
      boxShadow: '0 4px 15px rgba(236, 72, 153, 0.3)',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
      display: results ? 'block' : 'none',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const resultHighlightStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      textAlign: 'center' as const,
      border: '2px solid #f9a8d4',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const dateLabelStyle = {
    fontSize: '0.9rem',
    color: colors.accent,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.5rem',
  };

  const dateValueStyle = getResponsiveStyle(
    { color: colors.accent, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      borderRadius: '0.75rem',
      borderLeft: `4px solid ${colors.primary}`,
    },
    { padding: '1rem' },
    { padding: '1.125rem' },
    { padding: '1.25rem' }
  );

  const resultLabelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    color: colors.textSecondary,
    fontWeight: 600,
    marginBottom: '0.5rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  };

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const trimesterInfoStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #dbeafe 0%, #e0f2fe 100%)',
      borderRadius: '0.75rem',
      marginTop: '1.5rem',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const trimesterItemStyle = {
    display: 'flex',
    justifyContent: 'space-between' as const,
    padding: '0.75rem 0',
    borderBottom: '1px solid #bae6fd',
  };

  const trimesterNameStyle = {
    fontWeight: 600,
    color: '#0c4a6e',
  };

  const trimesterDatesStyle = {
    color: '#0369a1',
    fontSize: '0.9rem',
  };

  const infoNoteStyle = getResponsiveStyle(
    {
      background: '#fef3c7',
      borderLeft: '4px solid #f59e0b',
      borderRadius: '0.5rem',
      marginTop: '1.5rem',
      color: '#78350f',
      lineHeight: 1.6,
    },
    { padding: '1rem', fontSize: '0.875rem' },
    { padding: '1rem 1.25rem', fontSize: '0.9rem' },
    { padding: '1rem 1.25rem', fontSize: '0.9rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const faqItemStyle = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const referenceStyle = {
    fontSize: '0.875rem',
    color: colors.textSecondary,
    marginBottom: '0.5rem',
    lineHeight: 1.5,
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
            <p style={summaryTextStyle}>{t('toolSubtitle')}</p>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={methodSelectorStyle}>
              <label style={labelStyle} htmlFor="calc-method">{t('estimateBasedOn')}</label>
              <select
                id="calc-method"
                style={selectStyle}
                value={calculationMethod}
                onChange={(e) => setCalculationMethod(e.target.value)}
              >
                <option value="lmp">{t('lastMenstrualPeriod')}</option>
                <option value="ultrasound">{t('ultrasound')}</option>
                <option value="conception">{t('conceptionDate')}</option>
                <option value="ivf">{t('ivfTransferDate')}</option>
              </select>
            </div>

            {calculationMethod === 'lmp' && (
              <div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="lmp-date">{t('firstDayLastPeriod')}</label>
                  <input
                    type="date"
                    id="lmp-date"
                    style={inputStyle}
                    value={inputs.lmpDate}
                    onChange={(e) => setInputs({...inputs, lmpDate: e.target.value})}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="cycle-length">{t('averageCycleLength')}</label>
                  <select
                    id="cycle-length"
                    style={selectStyle}
                    value={inputs.cycleLength}
                    onChange={(e) => setInputs({...inputs, cycleLength: parseInt(e.target.value)})}
                  >
                    {[22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35].map(days => (
                      <option key={days} value={days}>{days} {t('daysLabel')}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {calculationMethod === 'ultrasound' && (
              <div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="ultrasound-date">{t('ultrasoundDate')}</label>
                  <input
                    type="date"
                    id="ultrasound-date"
                    style={inputStyle}
                    value={inputs.ultrasoundDate}
                    onChange={(e) => setInputs({...inputs, ultrasoundDate: e.target.value})}
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('pregnancyLengthAtTime')}</label>
                  <div style={inlineInputsStyle}>
                    <input
                      type="number"
                      min="0"
                      max="42"
                      placeholder={t('weeksPlaceholder')}
                      style={inputStyle}
                      value={inputs.ultrasoundWeeks}
                      onChange={(e) => setInputs({...inputs, ultrasoundWeeks: parseInt(e.target.value) || 0})}
                    />
                    <input
                      type="number"
                      min="0"
                      max="6"
                      placeholder={t('daysPlaceholder')}
                      style={inputStyle}
                      value={inputs.ultrasoundDays}
                      onChange={(e) => setInputs({...inputs, ultrasoundDays: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>
              </div>
            )}

            {calculationMethod === 'conception' && (
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="conception-date">{t('conceptionDateLabel')}</label>
                <input
                  type="date"
                  id="conception-date"
                  style={inputStyle}
                  value={inputs.conceptionDate}
                  onChange={(e) => setInputs({...inputs, conceptionDate: e.target.value})}
                />
              </div>
            )}

            {calculationMethod === 'ivf' && (
              <div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="ivf-date">{t('transferDate')}</label>
                  <input
                    type="date"
                    id="ivf-date"
                    style={inputStyle}
                    value={inputs.ivfDate}
                    onChange={(e) => setInputs({...inputs, ivfDate: e.target.value})}
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('embryoAge')}</label>
                  <div style={radioGroupStyle}>
                    <div style={radioOptionStyle}>
                      <input
                        type="radio"
                        id="ivf-day3"
                        name="ivf-age"
                        value="3"
                        checked={inputs.ivfEmbryoAge === 3}
                        onChange={() => setInputs({...inputs, ivfEmbryoAge: 3})}
                        style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.5rem', cursor: 'pointer', accentColor: colors.primary }}
                      />
                      <label htmlFor="ivf-day3" style={{ margin: 0, cursor: 'pointer', fontWeight: 500 }}>{t('day3')}</label>
                    </div>
                    <div style={radioOptionStyle}>
                      <input
                        type="radio"
                        id="ivf-day5"
                        name="ivf-age"
                        value="5"
                        checked={inputs.ivfEmbryoAge === 5}
                        onChange={() => setInputs({...inputs, ivfEmbryoAge: 5})}
                        style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.5rem', cursor: 'pointer', accentColor: colors.primary }}
                      />
                      <label htmlFor="ivf-day5" style={{ margin: 0, cursor: 'pointer', fontWeight: 500 }}>{t('day5')}</label>
                    </div>
                    <div style={radioOptionStyle}>
                      <input
                        type="radio"
                        id="ivf-day6"
                        name="ivf-age"
                        value="6"
                        checked={inputs.ivfEmbryoAge === 6}
                        onChange={() => setInputs({...inputs, ivfEmbryoAge: 6})}
                        style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.5rem', cursor: 'pointer', accentColor: colors.primary }}
                      />
                      <label htmlFor="ivf-day6" style={{ margin: 0, cursor: 'pointer', fontWeight: 500 }}>{t('day6')}</label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button type="submit" style={calculateButtonStyle}>
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={resultsStyle} id="results-section">
              <h3 style={{ ...h2Style, textAlign: 'center' as const }}>{t('yourPregnancyTimeline')}</h3>

              <div style={resultHighlightStyle}>
                <div style={dateLabelStyle}>{t('estimatedDueDate')}</div>
                <div style={dateValueStyle}>{results.dueDate}</div>
              </div>

              <div style={resultGridStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('currentWeek')}</span>
                  <span style={resultValueStyle}>{results.currentWeek}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('currentTrimester')}</span>
                  <span style={resultValueStyle}>{results.currentTrimester}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('daysUntilDueDate')}</span>
                  <span style={resultValueStyle}>{results.daysRemaining}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('conceptionDateLabel')}</span>
                  <span style={resultValueStyle}>{results.conceptionEstimate}</span>
                </div>
              </div>

              <div style={trimesterInfoStyle}>
                <h4 style={{ ...h3Style, fontSize: '1.1rem', color: '#0c4a6e', marginTop: 0 }}>{t('trimesterMilestones')}</h4>
                <div style={trimesterItemStyle}>
                  <span style={trimesterNameStyle}>{t('firstTrimester')}</span>
                  <span style={trimesterDatesStyle}>{results.trimester1}</span>
                </div>
                <div style={trimesterItemStyle}>
                  <span style={trimesterNameStyle}>{t('secondTrimester')}</span>
                  <span style={trimesterDatesStyle}>{results.trimester2}</span>
                </div>
                <div style={{ ...trimesterItemStyle, borderBottom: 'none' }}>
                  <span style={trimesterNameStyle}>{t('thirdTrimester')}</span>
                  <span style={trimesterDatesStyle}>{results.trimester3}</span>
                </div>
              </div>

              <div style={infoNoteStyle}>
                <strong>{t('noteLabel')}</strong> {t('noteText')}
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <h3 style={h3Style}>{t('calculationMethodsTitle')}</h3>
        <p style={paragraphStyle}>{t('calculationMethodsIntro')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t('lmpMethodTitle')}</strong> {t('lmpMethodDesc')}</li>
          <li style={listItemStyle}><strong>{t('ultrasoundMethodTitle')}</strong> {t('ultrasoundMethodDesc')}</li>
          <li style={listItemStyle}><strong>{t('conceptionMethodTitle')}</strong> {t('conceptionMethodDesc')}</li>
          <li style={listItemStyle}><strong>{t('ivfMethodTitle')}</strong> {t('ivfMethodDesc')}</li>
        </ul>

        <h3 style={h3Style}>{t('accuracyTitle')}</h3>
        <p style={paragraphStyle}>{t('accuracyParagraph1')}</p>
        <p style={paragraphStyle}>{t('accuracyParagraph2')}</p>

        <h3 style={h3Style}>{t('trimestersTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t('firstTrimesterTitle')}</strong> {t('firstTrimesterDesc')}</li>
          <li style={listItemStyle}><strong>{t('secondTrimesterTitle')}</strong> {t('secondTrimesterDesc')}</li>
          <li style={listItemStyle}><strong>{t('thirdTrimesterTitle')}</strong> {t('thirdTrimesterDesc')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('naegelesRuleTitle')}</h3>
        <p style={paragraphStyle}>{t('naegelesRuleDesc')}</p>

        <h3 style={h3Style}>{t('cycleLengthTitle')}</h3>
        <p style={paragraphStyle}>{t('cycleLengthDesc')}</p>

        <h3 style={h3Style}>{t('ultrasoundDatingTitle')}</h3>
        <p style={paragraphStyle}>{t('ultrasoundDatingDesc')}</p>

        <h3 style={h3Style}>{t('ivfDatingTitle')}</h3>
        <p style={paragraphStyle}>{t('ivfDatingDesc')}</p>

        <h3 style={h3Style}>{t('importantConsiderationsTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('consideration1')}</li>
          <li style={listItemStyle}>{t('consideration2')}</li>
          <li style={listItemStyle}>{t('consideration3')}</li>
          <li style={listItemStyle}>{t('consideration4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq6Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <div style={referenceStyle}>{t('reference1')}</div>
        <div style={referenceStyle}>{t('reference2')}</div>
        <div style={referenceStyle}>{t('reference3')}</div>
        <div style={referenceStyle}>{t('reference4')}</div>
        <div style={referenceStyle}>{t('reference5')}</div>
      </section>
    </div>
  );
}